/**
 * Zoom arithmetic for the file surfaces in the side panel. Pure, so the
 * bounds, the wheel curve and the PDF viewer address are testable without a
 * DOM; `useSurfaceZoom` owns the state and the listeners.
 *
 * @module components/files/previewZoom
 */

export const PREVIEW_ZOOM_MIN = 0.5;
export const PREVIEW_ZOOM_MAX = 4;
/** One click of the +/− buttons. */
export const PREVIEW_ZOOM_STEP = 1.25;

/** `min` lets a surface that is already fitted (a change crop) refuse to shrink below 100%. */
export function clampPreviewZoom(zoom: number, min = PREVIEW_ZOOM_MIN): number {
  if (!Number.isFinite(zoom)) return 1;
  const clamped = Math.min(PREVIEW_ZOOM_MAX, Math.max(min, zoom));
  // Two decimals keep 1.25 × 0.8 landing back on 1 instead of 0.9999….
  return Math.round(clamped * 100) / 100;
}

export const zoomInStep = (zoom: number, min = PREVIEW_ZOOM_MIN): number =>
  clampPreviewZoom(zoom * PREVIEW_ZOOM_STEP, min);
export const zoomOutStep = (zoom: number, min = PREVIEW_ZOOM_MIN): number =>
  clampPreviewZoom(zoom / PREVIEW_ZOOM_STEP, min);

/**
 * The multiplier a wheel or trackpad-pinch delta applies to the current zoom,
 * the curve the chat's zoomable image uses: `deltaMode` 1 is lines, 2 is
 * pages, everything else pixels; a pinch reports small pixel deltas, so the
 * exponent is gentle.
 */
export function wheelZoomFactor(
  deltaY: number,
  deltaMode: number,
  viewportHeight: number,
): number {
  const pixels = deltaY * (deltaMode === 1 ? 16 : deltaMode === 2 ? viewportHeight : 1);
  return Math.exp(-pixels * 0.01);
}

/** "125%" for the header label and the live region. */
export const formatPreviewZoom = (zoom: number): string => `${Math.round(zoom * 100)}%`;

export type PreviewZoomAction = "in" | "out" | "reset";

/**
 * The keyboard shortcuts every browser and editor uses for zoom: Ctrl/⌘ with
 * `=` or `+` in, `-` out, `0` back to 100%. Anything else, including the
 * bare keys the transcript's image viewer accepts, is left alone so typing in
 * an editable file is never hijacked.
 */
export function previewZoomShortcut(event: {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
}): PreviewZoomAction | null {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return null;
  switch (event.key) {
    case "=":
    case "+":
      return "in";
    case "-":
    case "_":
      return "out";
    case "0":
      return "reset";
    default:
      return null;
  }
}

/**
 * The address for a browser PDF viewer at a given zoom. Chrome's and
 * Firefox's viewers both read `zoom=<percent>` from the fragment — but only
 * when the document loads; a fragment-only change afterwards is ignored, so
 * callers key the frame on the zoom and let it re-mount. (Scaling the frame
 * with CSS `zoom` does not work either: the viewer refits the page to the
 * smaller viewport and only its chrome grows.) At 100% the caller's default
 * view (fit to width, or the viewer's own) applies.
 */
export function pdfViewerSrc(
  src: string,
  zoom: number,
  options: { readonly toolbar?: boolean; readonly fitWidth?: boolean } = {},
): string {
  const base = src.split("#", 1)[0] ?? src;
  const params: string[] = [];
  if (options.toolbar === false) params.push("toolbar=0");
  if (zoom !== 1) params.push(`zoom=${Math.round(zoom * 100)}`);
  else if (options.fitWidth) params.push("view=FitH");
  return params.length > 0 ? `${base}#${params.join("&")}` : base;
}

/**
 * How an `<img>` inside a scrolling, flex-centred box takes the zoom. At
 * 100% it is capped to the box on both axes (fit). Below 100% the same caps
 * shrink with the value, so a tall image never grows past its fitted size
 * when the person zooms out. Above 100% the image is sized to the value,
 * uncapped and unshrinkable, so it overflows into the scroll container
 * instead of being flexed back to the panel width.
 */
export function imageZoomPresentation(zoom: number): {
  /** Where the box aligns the image: centred while it fits, top-left once it can overflow. */
  readonly containerClassName: string;
  readonly imageClassName: string;
  readonly imageStyle: { readonly width?: string; readonly maxWidth?: string; readonly maxHeight?: string } | undefined;
} {
  const percent = `${Math.round(zoom * 100)}%`;
  if (zoom > 1) {
    return {
      containerClassName: "items-start justify-start",
      imageClassName: "max-w-none shrink-0 object-contain",
      imageStyle: { width: percent },
    };
  }
  return {
    containerClassName: "items-center justify-center",
    imageClassName: "max-h-full max-w-full object-contain",
    imageStyle: zoom === 1 ? undefined : { maxWidth: percent, maxHeight: percent },
  };
}
